import MixcloudPictures from './mixcloudPictures';
import MixcloudTag from './mixcloudTag';
import MixcloudUser from './mixcloudUser';

/**
 * A cloudcast - Mixcloud's word for a show, and what Fadila plays as a
 * track. Shapes taken from live responses against api.mixcloud.com
 * (docs/MIXCLOUD.md section 1); the fields Fadila reads are typed strictly
 * and the rest loosely.
 *
 * `key` is the path form - `/DeepHouseAmsterdam/theus-mago-dha-mix-346/` -
 * and it is the whole identity: the API addresses shows by it, and it is
 * what the player widget's `feed` parameter takes. There is no numeric id.
 *
 * There is no artist field, as there is none on SoundCloud either. `user` is
 * the account that uploaded the show and `hosts` are the credited DJs when
 * the uploader is a station rather than the artist.
 */
export default interface MixcloudCloudcast {
  /** `/user/slug/` - the id, the API path and the widget's feed. */
  key: string;
  /** The show's page on mixcloud.com. */
  url: string;
  name: string;
  slug?: string;
  /** Seconds, not milliseconds. Absent on a show that is still processing. */
  audio_length?: number;
  /** ISO 8601, unlike SoundCloud's dialect - `new Date()` reads it anywhere. */
  created_time?: string;
  updated_time?: string;
  /** Mixcloud has no genre; the first tag is the nearest thing to one. */
  tags?: MixcloudTag[];
  pictures?: MixcloudPictures;
  user: MixcloudUser;
  /** Credited DJs when the uploader is a station or a series. */
  hosts?: MixcloudUser[];
  description?: string | null;
  play_count?: number;
  favorite_count?: number;
  listener_count?: number;
  repost_count?: number;
  comment_count?: number;
  picture_primary_color?: string;
  /**
   * The tracklist, with a `start_time` in seconds per entry. Only the single
   * -show endpoint returns it and it is empty on most shows. Fadila reads
   * none of it - there is nowhere in a 100px player bar to put a tracklist -
   * so it stays loose. See docs/MIXCLOUD.md section 6.
   */
  sections?: unknown[];
}

/** True for something from the API that is a show rather than a user. */
export function isCloudcast(resource: any): resource is MixcloudCloudcast {
  return (
    !!resource &&
    typeof resource.key === 'string' &&
    !!resource.user &&
    typeof resource.username !== 'string'
  );
}
